import { FC, useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from 'components/UI/Button';
import { InputField } from 'components/UI/InputField';
import { Modal } from 'components/UI/Modal';
import { useTranslations } from 'hooks/useTranslations';
import { ExtendedUser } from 'models/diam';
import { useUpdateUserMutation } from 'store/diamApi';
import { nameRegex } from 'utils/constants';
import { showSuccessToast } from 'utils/toastUtils';
import { InputFieldContainer } from './styled';

type Props = {
  user: ExtendedUser;
  isOpen: boolean;
  close: () => void;
};

export const EditNameModal: FC<Props> = ({ user, isOpen, close }) => {
  const t = useTranslations();
  const [updateUser] = useUpdateUserMutation();

  // Zod schema
  const schema = useMemo(
    () =>
      z.object({
        firstName: z
          .string()
          .min(1, t('validation.required'))
          .max(50, t('validation.maximum_length', { length: 50 }))
          .regex(nameRegex, t('validation.name')),
        lastName: z
          .string()
          .min(1, t('validation.required'))
          .max(50, t('validation.maximum_length', { length: 50 }))
          .regex(nameRegex, t('validation.name'))
      }),
    [t]
  );

  type FormData = z.infer<typeof schema>;

  // useForm
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  // Close
  const onClose = useCallback(() => close(), [close]);

  // Submit
  const onSubmit: SubmitHandler<FormData> = useCallback(
    ({ firstName, lastName }) => {
      updateUser({
        firstName: firstName.trim(),
        lastName: lastName.trim()
      });
      showSuccessToast(t('profile.name_update_successful'));
      onClose();
    },
    [onClose, t, updateUser]
  );

  return (
    <Modal
      title={t('profile.action_edit_name')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputFieldContainer>
          <InputField
            id="firstName"
            label={t('profile.label_first_name')}
            required
            defaultValue={user.firstName}
            placeholder={t('profile.placeholder_first_name')}
            error={errors.firstName}
            register={register('firstName')}
          />
          <InputField
            id="lastName"
            label={t('profile.label_last_name')}
            required
            defaultValue={user.lastName}
            placeholder={t('profile.placeholder_last_name')}
            error={errors.lastName}
            register={register('lastName')}
          />
        </InputFieldContainer>
        <Button type="submit">{t('profile.save')}</Button>
      </form>
    </Modal>
  );
};
